import { gql, type TypedDocumentNode } from "@apollo/client";
import type { PaginationVariables } from "@/components/Pagination/Pagination";
import type { CommissionIngestBatchNode, CommissionIngestBatchStatus } from "./types";

export interface CommissionIngestBatchesQueryResponse {
  commissionIngestBatches: {
    nodes: CommissionIngestBatchNode[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor?: string;
      endCursor?: string;
    };
  };
}

export interface CommissionIngestBatchesQueryVariables extends PaginationVariables {
  campaignId?: string;
  partnerId?: string;
  status?: CommissionIngestBatchStatus;
}

export const COMMISSION_INGEST_BATCHES_QUERY: TypedDocumentNode<
  CommissionIngestBatchesQueryResponse,
  CommissionIngestBatchesQueryVariables
> = gql`
  query CommissionIngestBatches(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $campaignId: ID
    $partnerId: ID
    $status: BackofficeCommissionIngestBatchStatus
  ) {
    commissionIngestBatches(
      first: $first
      after: $after
      last: $last
      before: $before
      campaignId: $campaignId
      partnerId: $partnerId
      status: $status
    ) {
      nodes {
        id
        status
        linesVersion
        sourceChecksum
        createdAt
        campaign {
          id
          editionYear
          partner {
            id
            name
          }
          program {
            id
            name
            slug
          }
        }
      }
      totalCount
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export interface CommissionIngestBatchQueryResponse {
  commissionIngestBatch: CommissionIngestBatchNode & {
    reviewLines: Array<{
      id: string;
      pgaId: string;
      amountCents: number;
      confidence: string;
      crmPersonId?: string | null;
      authorizedFacilityName?: string | null;
      issues: Array<{ code: string; message: string }>;
    }>;
  };
}

export const COMMISSION_INGEST_BATCH_QUERY: TypedDocumentNode<CommissionIngestBatchQueryResponse, { id: string }> = gql`
  query CommissionIngestBatch($id: ID!) {
    commissionIngestBatch(id: $id) {
      id
      status
      linesVersion
      sourceChecksum
      createdAt
      campaign {
        id
        editionYear
        partner {
          id
          name
        }
        program {
          id
          name
          slug
        }
      }
      reviewLines {
        id
        pgaId
        amountCents
        confidence
        crmFacilityId
        crmPersonId
        sourceRowReference
        authorizedFacilityName
        issues {
          code
          message
        }
      }
    }
  }
`;

export interface CommissionIngestReviewPersonQueryResponse {
  person: {
    id: string;
    displayName: string;
    pgaId?: string | null;
    commissionBenefitProgram: {
      enrollment?: {
        id: string;
        accountStatus: string;
      } | null;
      programConsents: {
        totalCount: number;
      };
      allocationAgreements: {
        totalCount: number;
      };
    };
  };
}

export interface CommissionIngestReviewPersonQueryVariables {
  pgaId: string;
  campaignId?: string;
  programSlug?: string;
}

export const COMMISSION_INGEST_REVIEW_PERSON_QUERY: TypedDocumentNode<
  CommissionIngestReviewPersonQueryResponse,
  CommissionIngestReviewPersonQueryVariables
> = gql`
  query CommissionIngestReviewPerson($pgaId: ID!, $campaignId: ID, $programSlug: String) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
      commissionBenefitProgram(programSlug: $programSlug) {
        enrollment {
          id
          accountStatus
        }
        programConsents(first: 1, campaignId: $campaignId, status: ACTIVE) {
          totalCount
        }
        allocationAgreements(first: 1, campaignId: $campaignId, status: ACTIVE) {
          totalCount
        }
      }
    }
  }
`;
